import { differenceInDays, isAfter, isSameDay, startOfDay } from 'date-fns';
import type { EncounterStatusHistory } from 'fhir/r4';

import { getDurationInDays, type Period, type PeriodComplete } from './date';

export const hasEnd = (period: Period): period is PeriodComplete =>
  'end' in period;
export const hasStart = (period: Period): period is PeriodComplete =>
  'start' in period;

/**
 * Note that the period duration days are calculated such that the first day is "Day 1"
 */
export const getPeriodDurationDays = (period?: Period): number | undefined => {
  const isComplete = period && hasEnd(period);
  const days = getDurationInDays(
    period?.start,
    isComplete ? period?.end : undefined,
  );

  if (days === undefined) {
    return undefined;
  }

  return days >= 0 ? days + 1 : days;
};

const getPeriod = (statusHistory: EncounterStatusHistory) => {
  const endDate = startOfDay(
    statusHistory.status === 'in-progress' || !statusHistory.period.end
      ? new Date()
      : new Date(statusHistory.period.end as string),
  );

  const startDate = startOfDay(new Date(statusHistory.period.start as string));

  return {
    start: startDate,
    end: endDate,
  };
};

const getInterval = (statusHistory: EncounterStatusHistory[]) => {
  if (!statusHistory.length) {
    return undefined;
  }
  const first = statusHistory[0];
  const size = statusHistory.length;

  let last = statusHistory[size - 1];

  if (last.status === 'onleave') {
    last = statusHistory[size - 2];
  }

  return {
    start: new Date(first.period.start as string),
    end: new Date(last.period.end || Date.now()),
  };
};

export const getPeriodDayWithPausePeriod = (
  statusHistory: EncounterStatusHistory[],
  notePeriod?: Period,
): number | undefined => {
  const interval = getInterval(statusHistory);
  if (!interval) {
    return undefined;
  }
  const { start, end } = interval;

  const isComplete = notePeriod && hasEnd(notePeriod);
  const noteEndDate = isComplete ? new Date(notePeriod?.end) : end;

  const daysInPeriod = differenceInDays(noteEndDate, start);

  let onLeaveCount = 0;

  const onLeaveDays = statusHistory.reduce((amount, current) => {
    if (current.status !== 'onleave') {
      return amount;
    }

    const period = getPeriod(current);

    if (isAfter(period.end, noteEndDate)) {
      return amount;
    }

    if (!isSameDay(period.start, period.end)) {
      onLeaveCount++;
    }

    const days = differenceInDays(period.end, period.start);
    return amount + days;
  }, 0);

  return daysInPeriod - onLeaveDays + onLeaveCount + 1;
};
